import * as THREE from "three";
import { LevelSim, hitTouches } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";
import { FLYER_HEIGHT } from "./common";
import type { PooledVfx } from "../vfxPool";
import type { ProjectileHandle } from "../../../../../art/vfx.js";
import { HEAL_COLOUR, PetFxLook, petFxLook } from "./petFxLook";
import { PetStatusFx } from "./petStatusFx";
import { PET_STRIKE_TUNING } from "./petStrike";

/** How the pet's abilities show (world units, seconds). */
export const PET_ABILITY_FX = {
  /** A signature's wave: how many circles of motes run out to its reach, and their life (s). */
  rings: 2, ringLife: 0.55,
  /** The axolotl's spit: its size and the rise of its arc (its flight is petStrike.ts spitFlight). */
  spitSize: 0.24, spitArc: 0.25,
  /** Moonfall and Starfall: motes falling on each raider from this high. */
  fallHeight: 1.4,
  /**
   * The edge of the cells a pulse or a signature reaches, drawn as motes on the ground round it (each at the ground's
   * own height): this many, this big. A ground decal that wide (6 cells) was cut by cliffs and bridges into white
   * wedges and washed the field, so there is none. A raider it hits gets a small glow (`hitGlow`), the flag a small one.
   */
  edgeMotes: 18, edgeSize: 0.13, hitGlow: 0.35, glowSize: 0.5,
} as const;

const _p = new THREE.Vector3(), _q = new THREE.Vector3();
/** A spit in flight: its raider and the VFX module's live round (vfx.d.ts ProjectileHandle). */
interface Spit { readonly id: number; readonly round: ProjectileHandle }
/** Spits steered at once at most (one every strike: a handful in flight at the very most). */
const MAX_SPITS = 8;
const NOOP = (): void => {};
type Rgb = [number, number, number];
const rgb = (c: readonly [number, number, number]): Rgb => [c[0], c[1], c[2]];

/**
 * The pet's abilities made visible (Pets v2/v3, docs/PETS.md), presentation only, by its look (petFxLook.ts: the same
 * sim events for every pet). Its attack (PetBit) on the raider — the pup's snap, the owl's swoop, the kit's pounce of
 * flame, the tortoise's crystal bash, the axolotl's bubble, the magpie's peck; its pulse (PetBarked) round the flag —
 * Rally Bark's gold rings, the owl's lantern flare, the kit's ring of fire, the tortoise's ward and the axolotl's tide
 * pool rising on the troops, the magpie's glint; its signature (PetHowled) — a wave of motes out to the edge of its reach, moonlight or stars
 * falling on the raiders it hit, the tortoise's quake, the axolotl's bubble; and Mend (PetMended) on a troop. What lasts
 * (stuns, burns, marks, dazzles, troop rings) is PetStatusFx's. On the shared sprite VFX; no new shaders.
 */
export class PetAbilityFx {
  readonly group = new THREE.Group();
  readonly look: PetFxLook;
  readonly statuses: PetStatusFx;
  fx: (() => PooledVfx | null) | null = null;
  /** Where a troop / a raider is drawn (TroopViews.drawnAt, EnemyViews.feet). */
  troopAt: (id: number) => THREE.Vector3 | null = () => null;
  enemyAt: (id: number) => THREE.Vector3 | null = () => null;

  constructor(private readonly sim: LevelSim) {
    this.look = petFxLook(sim.mods.pet?.id ?? null);
    this.statuses = new PetStatusFx(sim, this.look);
    this.statuses.troopAt = (id) => this.troopAt(id);
    this.statuses.enemyAt = (id) => this.enemyAt(id);
    this.statuses.fx = () => this.fx?.() ?? null;
    this.group.add(this.statuses.group);
  }

  private get vfx(): PooledVfx | null { return this.fx?.() ?? null; }

  /**
   * Where the pet's blow lands on raider `id` (world, into `out`): its body over its feet, a flyer's up where it flies.
   * Null when it has no view.
   */
  hitPoint(id: number, out: THREE.Vector3): THREE.Vector3 | null {
    const at = this.enemyAt(id);
    if (!at) return null;
    const air = this.sim.findEnemy(id)?.isAir ?? false;
    return out.set(at.x, at.y + (air ? FLYER_HEIGHT : PET_STRIKE_TUNING.hitHeight), at.z);
  }

  /** The blow landed on raider `id` (petFight.ts): the bite's spark, the swoop's talon flash, the pounce's flame, the spit's pop. */
  bit(id: number): void {
    const fx = this.vfx, L = this.look, hit = this.hitPoint(id, _p);
    if (!hit || !fx) return;
    const c = rgb(L.attackColour);
    if (L.attack === "spit") { fx.bubbles(hit, 4, { color: c, radius: 0.18, size: 0.08, life: 0.5 }); return; }
    if (L.attack === "pounce") { fx.flames(hit, { life: 0.35, size: 0.35, color: c }); fx.embers(hit, 4, { color: c, radius: 0.15, life: 0.6, size: 0.07 }); return; }
    fx.sparks(hit, 6, { color: c, speed: L.attack === "swoop" ? 1.8 : 1.6, up: 1, life: 0.3, size: 0.1 });
  }

  /** The spit leaves `mouth` (world) for raider `id`, landing in `flight` seconds (it homes on the raider as it walks). */
  spit(id: number, mouth: THREE.Vector3, flight: number): void {
    const fx = this.vfx, c = rgb(this.look.attackColour);
    if (!fx || !this.hitPoint(id, _p)) return;
    // the pop is the blow's (bit, on the sim's held hit); the module copies `to`, so the round is steered by its handle
    const round = fx.projectile({ from: mouth, to: _p, life: flight, frame: "orb", size: PET_ABILITY_FX.spitSize, color: c, arc: PET_ABILITY_FX.spitArc, trail: true, onHit: NOOP });
    if (this.spits.length < MAX_SPITS) this.spits.push({ id, round });
  }

  /** Spits in flight, steered onto their raider each frame. */
  private readonly spits: Spit[] = [];

  /** The pulse went off round the flag (`rx`, `rz`): its rings and glow; the troop rings flare. */
  barked(rx = this.sim.troops.rally.x, rz = this.sim.troops.rally.y): void {
    this.statuses.flare();
    const fx = this.vfx, L = this.look, rule = this.sim.mods.pet;
    if (!fx || !rule || rule.bark) return;                     // Rally Bark shows on its troops' rings alone
    const reach = rule.pulse?.reach.toNumber() ?? 2, y = GroundMesh.drawnAt(rx, rz), c = rgb(L.pulseColour);
    this.edge(rx, rz, reach, c, 0.7);
    fx.glowPulse(_p.set(rx, y + 0.4, rz), { size: PET_ABILITY_FX.glowSize, life: 0.45, color: c });
    if (rule.pulse && hitTouches(rule.pulse.hit)) this.hits(rx, rz, reach, c);
    if (L.pulseFlames) for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      fx.flames(_p.set(rx + Math.cos(a) * reach * 0.8, y + 0.1, rz + Math.sin(a) * reach * 0.8), { life: 0.5, size: 0.35, color: c });
    }
    if (L.pulseRise) for (const t of this.sim.troops.all) {
      if (!t.alive || t.ownerTowerId !== 0) continue;
      const at = this.troopAt(t.id);
      if (at && Math.hypot(at.x - rx, at.z - rz) <= reach) fx.sparkles(_p.set(at.x, at.y + 0.3, at.z), 4, { color: c, radius: 0.2, rise: 0.9, life: 0.7, size: 0.12 });
    }
  }

  /** The signature from the pet at (x, z): its shockwave; `ids` the raiders it stunned. */
  howled(x: number, z: number, ids: readonly number[]): void {
    this.statuses.stun(ids);
    const fx = this.vfx, rule = this.sim.mods.pet, L = this.look;
    if (!fx || !rule) return;
    const reach = rule.howl?.reach.toNumber() ?? rule.signature?.reach.toNumber() ?? 2, y = GroundMesh.drawnAt(x, z), c = rgb(L.signatureColour);
    // the wave runs out from the pet to the edge of its reach: motes on a growing circle, then the edge
    for (let i = 0; i < PET_ABILITY_FX.rings; i++) {
      const r = reach * (i + 1) / PET_ABILITY_FX.rings, go = (): void => this.edge(x, z, r, c, PET_ABILITY_FX.ringLife);
      if (i === 0) go(); else fx.later(0.12 * i, go);
    }
    fx.glowPulse(_q.set(x, y + 0.3, z), { size: PET_ABILITY_FX.glowSize, life: 0.4, color: c });
    this.hits(x, z, reach, c);
    if (L.quake) { fx.dust(_q.set(x, y + 0.05, z), 8, { size: 0.3, speed: 1.6, life: 0.7 }); fx.debris(_q.set(x, y + 0.1, z), 10, { speed: 2.2, size: 0.1, color: c }); }
    if (L.fall || L.bubble) for (const e of this.sim.enemySlots) {
      if (!e.alive) continue;
      const p = this.enemyAt(e.id);
      if (!p || Math.hypot(p.x - x, p.z - z) > reach + 0.5) continue;
      if (L.fall) fx.sparkles(_q.set(p.x, p.y + PET_ABILITY_FX.fallHeight, p.z), 6, { color: c, radius: 0.25, rise: -1.2, life: 0.8, size: 0.16 });
      if (L.bubble && ids.includes(e.id)) fx.glowPulse(_q.set(p.x, p.y + 0.35, p.z), { size: PET_ABILITY_FX.glowSize * 0.7, life: 0.6, color: c });
    }
  }

  /** Motes round the circle of `r` cells about (x, z), each on the ground there: the edge of what an ability reaches. */
  private edge(x: number, z: number, r: number, c: Rgb, life: number): void {
    const fx = this.vfx, n = PET_ABILITY_FX.edgeMotes;
    if (!fx) return;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2, px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r;
      fx.sparkles(_q.set(px, GroundMesh.drawnAt(px, pz) + 0.12, pz), 1, { color: c, radius: 0.05, rise: 0.25, life, size: PET_ABILITY_FX.edgeSize });
    }
  }

  /** A small glow on each raider within `reach` of (x, z): the ones the ability touched. */
  private hits(x: number, z: number, reach: number, c: Rgb): void {
    const fx = this.vfx;
    if (!fx) return;
    for (const e of this.sim.enemySlots) {
      if (!e.alive) continue;
      const p = this.enemyAt(e.id);
      if (p && Math.hypot(p.x - x, p.z - z) <= reach) fx.glowPulse(_q.set(p.x, p.y + 0.3, p.z), { size: PET_ABILITY_FX.hitGlow, life: 0.4, color: c });
    }
  }

  /** Mend healed troop `id` (PetMended). */
  mended(id: number): void {
    const at = this.troopAt(id), fx = this.vfx;
    if (at && fx) fx.sparkles(_p.set(at.x, at.y + 0.3, at.z), 5, { color: rgb(HEAL_COLOUR), radius: 0.18, rise: 0.8, life: 0.7, size: 0.12 });
  }

  update(dt: number): void {
    this.statuses.update(dt);
    for (let i = this.spits.length - 1; i >= 0; i--) {
      const s = this.spits[i];
      if (s.round.age >= s.round.life || !this.hitPoint(s.id, s.round.to)) this.spits.splice(i, 1);
    }
  }

  dispose(): void { this.statuses.dispose(); this.spits.length = 0; this.group.clear(); }
}
